import { useCallback, useEffect, useMemo, useState } from 'react';
import Select from 'react-select';

import Axios from '../../../networking/intraceptor';
import { pharmacy_delivered_rx_reasons } from '../../../routes/routeName';
import {
	delivered_rx_reason_change_review_reasons,
	pending_delivered_rx_reason_change_reviews,
	pending_delivered_rx_reason_change_reviews_export,
	save_delivered_rx_reason_change_reviews,
} from '../../../networking/urlEndPoints';
import { APP_TIMEZONE } from '../../../utils/constants/formInitialStates';
import { safeToast } from '../../../utils/helper/helper';

export const DELIVERED_RX_REASON_SOCKET_EVENT = 'delivered-rx-reason-change-review';

// The reason list is no longer hard-coded here, and it is no longer derived from
// whatever staff typed in the past. It is the pharmacy's managed template list,
// curated on the Delivered RX Reasons page. Those three reasons that
// used to live in this file are seeded into every pharmacy's list by
// `php artisan pharmacy:backfill-delivered-rx-reason-templates --apply`.
export const DELIVERED_RX_REASON_SETTINGS_PATH = pharmacy_delivered_rx_reasons;
export const DELIVERED_RX_REASON_SNOOZE_OPTIONS = [
	{ value: '10', label: '10 min', minutes: 10 },
	{ value: '20', label: '20 min', minutes: 20 },
	{ value: '60', label: '1 hour', minutes: 60 },
	{ value: '120', label: '2 hours', minutes: 120 },
	{ value: '240', label: '4 hours', minutes: 240 },
	{ value: 'custom', label: 'Custom' },
];

const SNOOZE_STORAGE_PREFIX = 'deliveredRxReasonChangeReviewSnooze';

const getSnoozeStorageKey = (pharmacyId, userId) =>
	`${SNOOZE_STORAGE_PREFIX}:${pharmacyId || 'pharmacy'}:${userId || 'user'}`;

const getAppTimezoneParts = (value) => {
	const parts = new Intl.DateTimeFormat('en-US', {
		timeZone: APP_TIMEZONE,
		year: 'numeric',
		month: '2-digit',
		day: '2-digit',
		hour: '2-digit',
		minute: '2-digit',
		second: '2-digit',
		hour12: false,
	}).formatToParts(value);

	const partValue = (type) => parts.find((part) => part.type === type)?.value || '0';

	return {
		year: Number(partValue('year')),
		month: Number(partValue('month')),
		day: Number(partValue('day')),
		hour: Number(partValue('hour')) % 24,
		minute: Number(partValue('minute')),
		second: Number(partValue('second')),
	};
};

const getTimezoneOffsetMinutes = (value) => {
	const parts = getAppTimezoneParts(value);
	const appTimeAsUtc = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);
	return (appTimeAsUtc - value.getTime()) / 60000;
};

const appDateTimeLocalToDate = (dateTimeValue) => {
	if (!dateTimeValue) return null;

	const match = String(dateTimeValue).match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/);
	if (!match) return null;

	const [, year, month, day, hour, minute] = match.map(Number);
	const utcGuess = new Date(Date.UTC(year, month - 1, day, hour, minute, 0));
	const firstOffset = getTimezoneOffsetMinutes(utcGuess);
	const firstUtc = new Date(Date.UTC(year, month - 1, day, hour, minute, 0) - firstOffset * 60000);
	const secondOffset = getTimezoneOffsetMinutes(firstUtc);

	if (secondOffset !== firstOffset) {
		return new Date(Date.UTC(year, month - 1, day, hour, minute, 0) - secondOffset * 60000);
	}

	return firstUtc;
};

export const getAppDateTimeLocalValue = (value = new Date()) => {
	const parts = getAppTimezoneParts(value);
	const pad = (part) => String(part).padStart(2, '0');
	return `${parts.year}-${pad(parts.month)}-${pad(parts.day)}T${pad(parts.hour)}:${pad(parts.minute)}`;
};

export const getAppTimeLocalValue = (value = new Date()) => {
	const parts = getAppTimezoneParts(value);
	const pad = (part) => String(part).padStart(2, '0');
	return `${pad(parts.hour)}:${pad(parts.minute)}`;
};

const getAppDateTimeFromTimeValue = (timeValue) => {
	if (!timeValue) return '';

	const timeMatch = String(timeValue).match(/^(\d{2}):(\d{2})$/);
	if (!timeMatch) return '';

	const parts = getAppTimezoneParts(new Date());
	const pad = (part) => String(part).padStart(2, '0');
	return `${parts.year}-${pad(parts.month)}-${pad(parts.day)}T${timeMatch[1]}:${timeMatch[2]}`;
};

export const getDeliveredRxReasonSnoozeUntilDate = ({ minutes, customTime } = {}) => {
	const normalizedMinutes = Number(minutes);
	if (!Number.isNaN(normalizedMinutes) && normalizedMinutes > 0) {
		return new Date(Date.now() + normalizedMinutes * 60 * 1000);
	}

	const customTimeDate = appDateTimeLocalToDate(getAppDateTimeFromTimeValue(customTime));
	if (!customTimeDate) return null;

	if (customTimeDate.getTime() <= Date.now()) {
		return new Date(customTimeDate.getTime() + 24 * 60 * 60 * 1000);
	}

	return customTimeDate;
};

const getStoredSnoozeUntilMs = (storageKey) => {
	try {
		const storedValue = localStorage.getItem(storageKey);
		if (!storedValue) return null;

		const parsedValue = JSON.parse(storedValue);
		const snoozeUntilMs = new Date(parsedValue?.snoozeUntil || parsedValue).getTime();
		if (Number.isNaN(snoozeUntilMs) || snoozeUntilMs <= Date.now()) {
			localStorage.removeItem(storageKey);
			return null;
		}

		return snoozeUntilMs;
	} catch (_) {
		localStorage.removeItem(storageKey);
		return null;
	}
};

export const getDeliveredRxReasonRecordFormKey = (record, index = 0) => String(record?.id || `${record?.rx_number || 'rx'}-${record?.rxrefill_id || index}-${record?.reef_date || index}`);

const getRecordIdentityKey = (record) => (record?.id ? `id:${record.id}` : [
	record?.rx_number || record?.rxnumber || 'rx',
	record?.rxrefill_id || record?.refill_id || '',
	record?.reef_date || '',
	record?.previous_reason || '',
	record?.updated_reason || '',
].join('|'));

export const normalizeDeliveredReasonRecord = (record) => ({
	...record,
	rx_number: record?.rx_number || record?.rxnumber || '',
	rxrefill_id: record?.rxrefill_id || record?.refill_id || '',
	delivered_by: record?.delivered_by || '',
	delivered_at_display: record?.delivered_at_display || record?.delivery_time_display || record?.delivery_time || '',
	delivery_status_display: record?.delivery_status_display || record?.delivery_status || '',
	review_reason: record?.review_reason || '',
});

const mergeRecords = (currentRecords, incomingRecords) => {
	const merged = new Map();
	currentRecords.forEach((record) => merged.set(getRecordIdentityKey(record), record));
	incomingRecords.forEach((record) => merged.set(getRecordIdentityKey(record), record));
	return Array.from(merged.values());
};

const removeRecords = (currentRecords, acknowledgedRecords) => {
	const acknowledgedKeys = new Set(acknowledgedRecords.map(getRecordIdentityKey));
	return currentRecords.filter((record) => !acknowledgedKeys.has(getRecordIdentityKey(record)));
};

const collapseWhitespace = (value) => String(value ?? '').replace(/\s+/g, ' ').trim();

/**
 * Server payload -> react-select option.
 *
 * `label` is the managed TITLE (what the pharmacy picks from) and `value` is the
 * DESCRIPTION (the text that gets written onto the record). `templateId` is what
 * the save call sends so the backend can record which managed reason was chosen.
 */
const normalizeReviewReasonOption = (reason) => {
	if (reason && typeof reason === 'object') {
		const description = collapseWhitespace(reason.description || reason.value || reason.label);
		const title = collapseWhitespace(reason.title || reason.label || description);
		const templateId = reason.template_id ?? reason.id ?? null;

		return description ? { value: description, label: title || description, templateId } : null;
	}

	const normalizedReason = collapseWhitespace(reason);
	return normalizedReason ? { value: normalizedReason, label: normalizedReason, templateId: null } : null;
};

/**
 * De-dupe key for an option. Managed templates are identified by their id; the
 * fuzzy text matching that used to fold DB copies of the hard-coded reasons into
 * one entry is gone - the server list is now authoritative and already unique.
 */
const getDeliveredRxReasonOptionKey = (option) => (
	option?.templateId != null
		? `template:${option.templateId}`
		: `text:${collapseWhitespace(option?.value).toLowerCase()}`
);

const mergeDeliveredRxReasonOptions = (...optionGroups) => {
	const mergedOptions = [];
	const optionKeys = new Set();

	optionGroups.flat().forEach((option) => {
		const normalizedOption = normalizeReviewReasonOption(option);
		if (!normalizedOption) {
			return;
		}

		const optionKey = getDeliveredRxReasonOptionKey(normalizedOption);
		if (optionKeys.has(optionKey)) {
			return;
		}

		optionKeys.add(optionKey);
		mergedOptions.push(normalizedOption);
	});

	return mergedOptions;
};

/**
 * The current selection for a row. `selection` is what handleReasonChange stored:
 * { value, templateId }. Matching is by template id first, so a description edited
 * in settings still resolves to the right entry.
 */
const getDeliveredRxReasonReviewOption = (selection, reasonOptions = []) => {
	if (!selection) {
		return null;
	}

	const selectedValue = typeof selection === 'object' ? selection.value : selection;
	const selectedTemplateId = typeof selection === 'object' ? selection.templateId : null;

	const matchedOption = reasonOptions.find((option) => (
		selectedTemplateId != null ? option.templateId === selectedTemplateId : option.value === selectedValue
	));

	return matchedOption || (selectedValue ? { value: selectedValue, label: selectedValue, templateId: null } : null);
};

const deliveredRxReasonSelectStyles = {
	control: (base, state) => ({
		...base,
		minHeight: 40,
		minWidth: 320,
		borderColor: state.selectProps.hasError ? '#fca5a5' : state.isFocused ? '#3b82f6' : '#d1d5db',
		borderRadius: 6,
		boxShadow: state.selectProps.hasError
			? state.isFocused ? '0 0 0 1px #ef4444' : 'none'
			: state.isFocused ? '0 0 0 1px #3b82f6' : 'none',
		backgroundColor: state.isDisabled ? '#f1f5f9' : '#fff',
		':hover': {
			borderColor: state.selectProps.hasError ? '#f87171' : state.isFocused ? '#3b82f6' : '#9ca3af',
		},
	}),
	valueContainer: (base) => ({
		...base,
		padding: '2px 10px',
	}),
	placeholder: (base) => ({
		...base,
		color: '#64748b',
		fontSize: 14,
	}),
	input: (base) => ({
		...base,
		color: '#111827',
		fontSize: 14,
	}),
	singleValue: (base) => ({
		...base,
		color: '#111827',
		fontSize: 14,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	}),
	menuPortal: (base) => ({
		...base,
		zIndex: 220,
	}),
	menu: (base) => ({
		...base,
		width: 340,
		minWidth: 340,
		border: '1px solid #e5e7eb',
		borderRadius: 6,
		boxShadow: '0 10px 24px rgba(15, 23, 42, 0.14)',
	}),
	menuList: (base) => ({
		...base,
		padding: 4,
	}),
	option: (base, state) => ({
		...base,
		borderRadius: 4,
		color: '#111827',
		fontSize: 13,
		whiteSpace: 'nowrap',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		backgroundColor: state.isSelected ? '#dbeafe' : state.isFocused ? '#eff6ff' : '#fff',
		':active': {
			backgroundColor: '#dbeafe',
		},
	}),
};

const downloadBase64File = (base64Content, fileName, mimeType = 'text/csv') => {
	const binaryString = atob(base64Content);
	const bytes = new Uint8Array(binaryString.length);

	for (let index = 0; index < binaryString.length; index += 1) {
		bytes[index] = binaryString.charCodeAt(index);
	}

	const blob = new Blob([bytes], { type: mimeType });
	const fileUrl = URL.createObjectURL(blob);
	const link = document.createElement('a');
	link.href = fileUrl;
	link.download = fileName;
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
	URL.revokeObjectURL(fileUrl);
};

export const useDeliveredRxReasonChangeReviews = ({
	enabled = true,
	pharmacyId,
	userId,
	listenToSocket = true,
	showSuccessToast = true,
	enableSnooze = false,
} = {}) => {
	const [records, setRecords] = useState([]);
	const [reasons, setReasons] = useState({});
	const [fieldErrors, setFieldErrors] = useState({});
	const [submitting, setSubmitting] = useState(false);
	const [exporting, setExporting] = useState(false);
	const [loading, setLoading] = useState(false);
	const [hasLoaded, setHasLoaded] = useState(false);
	const [savedReasonOptions, setSavedReasonOptions] = useState([]);
	const [snoozeUntilMs, setSnoozeUntilMs] = useState(() => (
		enableSnooze ? getStoredSnoozeUntilMs(getSnoozeStorageKey(pharmacyId, userId)) : null
	));

	// Managed templates only. No local defaults are merged in any more - an empty
	// list means the pharmacy has not curated one yet, and the popup says so.
	const reasonOptions = useMemo(
		() => mergeDeliveredRxReasonOptions(savedReasonOptions),
		[savedReasonOptions]
	);

	const snoozeStorageKey = getSnoozeStorageKey(pharmacyId, userId);
	const isSnoozed = useCallback(() => {
		if (!enableSnooze) {
			return false;
		}

		const storedSnoozeUntilMs = getStoredSnoozeUntilMs(snoozeStorageKey);
		setSnoozeUntilMs(storedSnoozeUntilMs);
		return Boolean(storedSnoozeUntilMs);
	}, [enableSnooze, snoozeStorageKey]);

	const loadPendingRecords = useCallback(async () => {
		if (!enabled || !pharmacyId || !userId) {
			setRecords([]);
			setHasLoaded(true);
			return;
		}

		if (isSnoozed()) {
			setRecords([]);
			setHasLoaded(true);
			return;
		}

		setLoading(true);
		setHasLoaded(false);

		try {
			const response = await Axios.get(pending_delivered_rx_reason_change_reviews);
			const responseData = response?.data?.data || {};
			const nextRecords = Array.isArray(responseData.records)
				? responseData.records.map(normalizeDeliveredReasonRecord)
				: [];
			const shouldShowRecords = responseData.show_popup ?? nextRecords.length > 0;
			setRecords(shouldShowRecords && !isSnoozed() ? nextRecords : []);
		} catch (error) {
			if (error?.response?.status !== 401) {
				safeToast.error(error?.response?.data?.message || 'Unable to load delivered RX reason changes.');
			}
		} finally {
			setLoading(false);
			setHasLoaded(true);
		}
	}, [enabled, isSnoozed, pharmacyId, userId]);

	const loadSavedReasonOptions = useCallback(async () => {
		if (!enabled || !pharmacyId || !userId) {
			setSavedReasonOptions([]);
			return;
		}

		try {
			const response = await Axios.get(delivered_rx_reason_change_review_reasons);
			const responseData = response?.data?.data || {};
			// `templates` is the new managed list; `options` / `reasons` are the
			// legacy keys the endpoint still returns for older clients.
			const nextOptions = Array.isArray(responseData.templates)
				? responseData.templates
				: Array.isArray(responseData.options)
					? responseData.options
					: Array.isArray(responseData.reasons)
						? responseData.reasons
						: [];

			setSavedReasonOptions(mergeDeliveredRxReasonOptions(nextOptions));
		} catch (error) {
			if (error?.response?.status !== 401) {
				safeToast.error(error?.response?.data?.message || 'Unable to load delivered RX review reasons.');
			}
		}
	}, [enabled, pharmacyId, userId]);

	useEffect(() => {
		loadPendingRecords();
		loadSavedReasonOptions();
	}, [loadPendingRecords, loadSavedReasonOptions]);

	useEffect(() => {
		if (!enabled || !enableSnooze || !snoozeUntilMs) {
			return undefined;
		}

		const delay = snoozeUntilMs - Date.now();
		if (delay <= 0) {
			localStorage.removeItem(snoozeStorageKey);
			setSnoozeUntilMs(null);
			loadPendingRecords();
			return undefined;
		}

		const timer = setTimeout(() => {
			localStorage.removeItem(snoozeStorageKey);
			setSnoozeUntilMs(null);
			loadPendingRecords();
		}, delay);

		return () => clearTimeout(timer);
	}, [enabled, enableSnooze, loadPendingRecords, snoozeStorageKey, snoozeUntilMs]);

	useEffect(() => {
		setReasons((previousReasons) => {
			const nextReasons = {};
			records.forEach((record, index) => {
				const recordKey = getDeliveredRxReasonRecordFormKey(record, index);
				nextReasons[recordKey] = previousReasons[recordKey] ?? record.review_reason ?? '';
			});

			return nextReasons;
		});
		setFieldErrors({});
	}, [records]);

	useEffect(() => {
		if (!enabled || !pharmacyId || !userId || !listenToSocket) {
			setRecords([]);
			return undefined;
		}

		const handleSocketEvent = (event) => {
			const detail = event?.detail || {};
			if (detail.event !== DELIVERED_RX_REASON_SOCKET_EVENT) {
				return;
			}

			if (isSnoozed()) {
				setRecords([]);
				return;
			}

			const payload = Array.isArray(detail.payload) ? detail.payload[0] : detail.payload || {};
			const eventPharmacyId = Number(payload?.pharmacy_id);
			if (eventPharmacyId && Number(pharmacyId) && eventPharmacyId !== Number(pharmacyId)) {
				return;
			}

			const incomingRecords = Array.isArray(payload?.records)
				? payload.records.map(normalizeDeliveredReasonRecord)
				: [];

			if (!incomingRecords.length) {
				loadPendingRecords();
				return;
			}

			if (payload?.action === 'acknowledged') {
				setRecords((currentRecords) => removeRecords(currentRecords, incomingRecords));
				return;
			}

			setRecords((currentRecords) => mergeRecords(currentRecords, incomingRecords));
		};

		window.addEventListener('oatrx:realtime:event', handleSocketEvent);

		return () => {
			window.removeEventListener('oatrx:realtime:event', handleSocketEvent);
		};
	}, [enabled, isSnoozed, listenToSocket, loadPendingRecords, pharmacyId, userId]);

	// `option` is a managed template option or null when cleared. Free text can no
	// longer reach here - the select is not creatable any more.
	const handleReasonChange = useCallback((recordKey, option) => {
		const selection = option?.value
			? { value: option.value, templateId: option.templateId ?? null }
			: null;

		setReasons((previous) => ({ ...previous, [recordKey]: selection }));
		setFieldErrors((previous) => {
			if (!previous[recordKey]) {
				return previous;
			}

			const nextErrors = { ...previous };
			delete nextErrors[recordKey];
			return nextErrors;
		});
	}, []);

	const handleSkip = useCallback((snoozeConfig = {}) => {
		if (!enableSnooze) {
			setRecords([]);
			setReasons({});
			setFieldErrors({});
			return;
		}

		const snoozeUntilDate = getDeliveredRxReasonSnoozeUntilDate(snoozeConfig);
		const snoozeUntilTime = snoozeUntilDate?.getTime();

		if (!snoozeUntilDate || Number.isNaN(snoozeUntilTime) || snoozeUntilTime <= Date.now()) {
			return;
		}

		localStorage.setItem(snoozeStorageKey, JSON.stringify({
			snoozeUntil: snoozeUntilDate.toISOString(),
			timeZone: APP_TIMEZONE,
		}));
		setSnoozeUntilMs(snoozeUntilTime);
		setRecords([]);
		setReasons({});
		setFieldErrors({});
	}, [enableSnooze, snoozeStorageKey]);

	const handleExportCsv = useCallback(async () => {
		setExporting(true);

		try {
			const response = await Axios.get(pending_delivered_rx_reason_change_reviews_export);
			const exportData = response?.data?.data || {};
			const content = exportData?.content;

			if (!content) {
				safeToast.error('Unable to export delivered RX reason change reviews.');
				return;
			}

			downloadBase64File(
				content,
				exportData?.filename || 'pending-delivered-rx-reason-change-reviews.csv',
				exportData?.mime_type || 'text/csv'
			);
		} catch (error) {
			safeToast.error(error?.response?.data?.message || 'Unable to export delivered RX reason change reviews.');
		} finally {
			setExporting(false);
		}
	}, []);

	const handleSubmit = useCallback(async () => {
		if (!records.length) return;

		const recordsToSave = records.reduce((accumulator, record, index) => {
			const recordKey = getDeliveredRxReasonRecordFormKey(record, index);
			const selection = reasons[recordKey];
			const normalizedReason = collapseWhitespace(
				typeof selection === 'object' ? selection?.value : selection
			);

			if (normalizedReason) {
				accumulator.push({
					record,
					recordKey,
					review_reason: normalizedReason,
					reason_template_id: (typeof selection === 'object' ? selection?.templateId : null) ?? null,
				});
			}

			return accumulator;
		}, []);

		if (!recordsToSave.length) {
			setFieldErrors({});
			safeToast.error('Select at least one reason.');
			return;
		}

		setSubmitting(true);
		setFieldErrors({});

		try {
			// review_reason is still sent so the request stays valid against a backend
			// that has not been deployed yet; the backend prefers reason_template_id
			// and snapshots that template's description onto the record.
			await Axios.post(save_delivered_rx_reason_change_reviews, {
				records: recordsToSave.map(({ record, review_reason, reason_template_id }) => ({
					id: record.id,
					review_reason,
					reason_template_id,
				})),
			});

			const savedIds = new Set(recordsToSave.map(({ record }) => record.id));
			const savedKeys = new Set(recordsToSave.map(({ recordKey }) => recordKey));
			setRecords((currentRecords) => currentRecords.filter((record) => !savedIds.has(record.id)));
			setReasons((currentReasons) => {
				const nextReasons = { ...currentReasons };
				savedKeys.forEach((recordKey) => {
					delete nextReasons[recordKey];
				});
				return nextReasons;
			});
			if (showSuccessToast) {
				safeToast.success('Delivered RX reason change review saved.');
			}
			loadSavedReasonOptions();
			loadPendingRecords();
		} catch (error) {
			const errors = error?.response?.data?.data?.error;
			const reasonError = errors?.['records.0.review_reason']?.[0];
			setFieldErrors(reasonError && recordsToSave.length ? { [recordsToSave[0].recordKey]: reasonError } : {});
			safeToast.error(error?.response?.data?.message || 'Unable to save delivered RX reason change review.');
		} finally {
			setSubmitting(false);
		}
	}, [loadPendingRecords, loadSavedReasonOptions, reasons, records, showSuccessToast]);

	return {
		records,
		reasons,
		reasonOptions,
		fieldErrors,
		submitting,
		exporting,
		loading,
		hasLoaded,
		loadPendingRecords,
		handleReasonChange,
		handleExportCsv,
		handleSubmit,
		handleSkip,
		setRecords,
	};
};

const DeliveredRxReasonChangeReviewTable = ({
	records,
	reasons,
	reasonOptions = [],
	errors,
	submitting,
	onReasonChange,
}) => (
	<div className="overflow-auto">
		<table className="min-w-[1500px] table-fixed border-collapse border border-gray-200 text-left text-sm">
			<thead className="sticky top-0 z-10 bg-gray-50">
				<tr>
					<th className="w-[52px] whitespace-nowrap border border-gray-200 px-2 py-2 text-[11px] font-semibold uppercase tracking-wider text-gray-700">
						<span className="flex h-6 w-6 items-center justify-center rounded-full bg-gray-200 text-xs font-bold">#</span>
					</th>
					<th className="w-[140px] whitespace-nowrap border border-gray-200 px-2 py-2 text-[11px] font-semibold uppercase tracking-wider text-gray-700">Patient</th>
					<th className="w-[85px] whitespace-nowrap border border-gray-200 px-2 py-2 text-[11px] font-semibold uppercase tracking-wider text-gray-700">Rx</th>
					<th className="w-[145px] whitespace-nowrap border border-gray-200 px-2 py-2 text-[11px] font-semibold uppercase tracking-wider text-gray-700">Drug</th>
					<th className="w-[155px] whitespace-nowrap border border-gray-200 px-2 py-2 text-[11px] font-semibold uppercase tracking-wider text-gray-700">Delivery Time</th>
					<th className="w-[130px] whitespace-nowrap border border-gray-200 px-2 py-2 text-[11px] font-semibold uppercase tracking-wider text-gray-700">Delivered By</th>
					<th className="w-[100px] whitespace-nowrap border border-gray-200 px-2 py-2 text-[11px] font-semibold uppercase tracking-wider text-gray-700">Status</th>
					<th className="w-[110px] whitespace-nowrap border border-gray-200 px-2 py-2 text-[11px] font-semibold uppercase tracking-wider text-gray-700">Refill Date</th>
					<th className="w-[135px] whitespace-nowrap border border-gray-200 px-2 py-2 text-[11px] font-semibold uppercase tracking-wider text-gray-700">Prev Reason</th>
					<th className="w-[130px] whitespace-nowrap border border-gray-200 px-2 py-2 text-[11px] font-semibold uppercase tracking-wider text-gray-700">Updated Reason</th>
					<th className="w-[360px] whitespace-nowrap border border-gray-200 px-2 py-2 text-[11px] font-semibold uppercase tracking-wider text-gray-700">Reason</th>
				</tr>
			</thead>
			<tbody className="bg-white">
				{records.map((record, index) => {
					const recordKey = getDeliveredRxReasonRecordFormKey(record, index);
					const error = errors[recordKey];

					return (
						<tr key={recordKey} className="align-top transition-colors hover:bg-gray-50">
							<td className="border border-gray-200 px-2 py-3">
								<span className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-100 text-xs font-bold text-blue-800">
									{index + 1}
								</span>
							</td>
							<td className="border border-gray-200 px-2 py-3">
								<span className="text-sm font-semibold text-gray-900">{record.patient_name || ''}</span>
							</td>
							<td className="border border-gray-200 px-2 py-3">
								<span className="text-sm font-semibold text-gray-900">{record.rx_number || '-'}</span>
							</td>
							<td className="border border-gray-200 px-2 py-3">
								<span className="text-sm font-medium text-gray-900">{record.drug || '-'}</span>
							</td>
							<td className="whitespace-nowrap border border-gray-200 px-2 py-3">
								<span className="text-sm font-medium text-gray-900">{record.delivered_at_display || record.delivered_at || '-'}</span>
							</td>
							<td className="border border-gray-200 px-2 py-3">
								<span className="text-sm font-medium text-gray-900">{record.delivered_by || '-'}</span>
							</td>
							<td className="whitespace-nowrap border border-gray-200 px-2 py-3">
								<span className="text-sm font-semibold text-gray-900">{record.delivery_status_display || '-'}</span>
							</td>
							<td className="whitespace-nowrap border border-gray-200 px-2 py-3">
								<span className="text-sm font-medium text-gray-900">{record.formatted_reef_date || record.reef_date || '-'}</span>
							</td>
							<td className="border border-gray-200 px-2 py-3">
								<span className="text-sm font-medium text-red-700">{record.previous_reason || '-'}</span>
							</td>
							<td className="border border-gray-200 px-2 py-3">
								<span className="text-sm font-semibold text-red-700">{record.updated_reason || '-'}</span>
							</td>
							<td className="border border-gray-200 px-2 py-3">
								{reasonOptions.length === 0 ? (
									<div className="min-w-[320px] rounded border border-dashed border-gray-300 bg-gray-50 px-3 py-2">
										<p className="text-sm font-medium text-gray-700">No reasons set up yet.</p>
										<p className="mt-1 text-xs text-gray-500">
											Add them under{' '}
											<a
												href={DELIVERED_RX_REASON_SETTINGS_PATH}
												className="font-semibold text-blue-600 underline"
											>
											Delivered RX Reasons
											</a>
											, then reopen this popup.
										</p>
									</div>
								) : (
									<Select
										inputId={`delivered-rx-reason-change-review-${recordKey}`}
										value={getDeliveredRxReasonReviewOption(reasons[recordKey], reasonOptions)}
										onChange={(option) => onReasonChange(recordKey, option)}
										options={reasonOptions}
										styles={deliveredRxReasonSelectStyles}
										hasError={Boolean(error)}
										isClearable
										isDisabled={submitting}
										isSearchable
										classNamePrefix="react-select"
										menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
										menuPosition="fixed"
										placeholder="Select reason"
										noOptionsMessage={() => 'No matching reason'}
									/>
								)}
								{error && <p className="mt-2 text-sm text-red-600">{error}</p>}
							</td>
						</tr>
					);
				})}
			</tbody>
		</table>
	</div>
);

export default DeliveredRxReasonChangeReviewTable;
